/**
 * Issuing a DRAFT document: the transition that makes it real.
 *
 * WHY THIS FILE HAD TO EXIST
 * -------------------------
 * `issueDocument` CREATES a document. It does not transition one. The lifecycle
 * in `domain/lifecycle.ts` is explicit that a document is drafted and then
 * issued, and `canIssue` refuses anything that is not a DRAFT — so there was a
 * moment where a user could create an invoice and then do nothing at all with
 * it. No screen could call `issueDocument` directly either: it takes a live
 * transaction, and RULE 4 bans `dexie` outside `data/`. This is the same seam
 * `invoiceService` was built for.
 *
 * WHAT THE TRANSITION DOES, AND WHY IT IS NOT JUST A STATUS WRITE
 * --------------------------------------------------------------
 * Issuing is the moment the document stops being reversible-by-deletion and
 * becomes a tax document, so the transition is guarded by the SAME pure
 * `canIssue` the engine uses, in the SAME transaction as the write. A status
 * flip that succeeded without the guard would be a status flip nobody checked.
 */
import Dexie, { type Table } from 'dexie';
import { canIssue } from '@domain/lifecycle';
import { docSpec, type DocType } from '@domain/docTypes';
import { resolvePlaceOfSupply } from '@core/gst/split';
import { inverseDirection, movementReasonFor, stockOwnerFor } from '@domain/stockPolicy';
import { ulid } from '../../platform/ids';
import type {
  AuditLog,
  Business,
  Document,
  DocumentLine,
  DocumentLink,
  Product,
  StockTransaction,
  SyncOutboxEntry,
} from '../db/types';
import { recordBusinessMutations, nextVersion } from '../sync/recordBusinessMutation';

/**
 * Why an issue was refused.
 *
 * `NOT_ISSUABLE` covers every guard the lifecycle refused, including "already
 * issued" — the guard's own `code` is deliberately not re-exported here, because
 * these are the codes a CALLER can branch on and the guard's are not part of this
 * service's contract. The message is preserved either way, which is what the UI
 * shows.
 */
export type IssueTransitionErrorCode =
  | 'UNKNOWN_DOCUMENT'
  | 'NOT_ISSUABLE'
  /** The document cannot be issued until the user supplies a place of supply. */
  | 'PLACE_OF_SUPPLY_REQUIRED'
  /** Only an ISSUED document can be cancelled; a draft is deleted, not cancelled. */
  | 'NOT_CANCELLABLE';

export class IssueTransitionError extends Error {
  constructor(
    readonly code: IssueTransitionErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'IssueTransitionError';
  }
}

/**
 * The tables this transition writes. Explicit, so a missing one is a hard error
 * rather than a silent partial issue.
 *
 * `products` and `stockTransactions` are here because ISSUING is the event that
 * posts stock (the owner decision: a draft never does). Without them in the
 * transaction scope, Dexie would refuse the write mid-way and the document would
 * be left issued with no movement — the worst possible state, because every
 * total still balances and only the stock ledger is wrong.
 */
export interface IssueTransitionTables {
  documents: Table<Document, string>;
  documentLines: Table<DocumentLine, string>;
  auditLogs: Table<AuditLog, string>;
  businesses: Table<Business, string>;
  stockTransactions: Table<StockTransaction, string>;
  products: Table<Product, string>;
  /** Needed to tell a delivered-and-invoiced line from a direct one. */
  documentLinks: Table<DocumentLink, string>;
  /**
   * The sync outbox, in the SAME transaction.
   *
   * Required because this is the path that moves stock. An issue that commits
   * locally and is never published is a stock movement only one device knows
   * about, and a Delivery Note's ownership of a movement is precisely what stops
   * the following invoice deducting the same goods again.
   */
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface IssueTransitionResult {
  readonly id: string;
  readonly docNumber: string;
  readonly status: 'ISSUED';
}

/**
 * Move a DRAFT document to ISSUED, posting the stock movement the DRAFT withheld.
 *
 * THIS IS WHERE STOCK MOVES. The owner decision is that a DRAFT never changes
 * stock, so `issueDocument` writes no movements when it saves a draft, and this
 * transition is the event that posts them. Without this the app would deduct
 * nothing, ever — which is a quieter failure than double-deducting, because every
 * number still balances.
 *
 * The rule itself is NOT repeated here. `stockOwnerFor` is the single definition,
 * and `issueDocument` calls it too, so the create path and the issue path cannot
 * disagree about whether a line is the document's to move.
 */
export async function issueDraftDocument(
  tx: IssueTransitionTables,
  documentId: string,
  user: { id: string; name: string },
  now: string,
  deviceId: string,
): Promise<IssueTransitionResult> {
  const doc = await tx.documents.get(documentId);
  if (!doc) {
    throw new IssueTransitionError('UNKNOWN_DOCUMENT', `No document with id "${documentId}".`);
  }

  const lines = await tx.documentLines.where('documentId').equals(documentId).toArray();

  // The SAME guard the engine applies, re-read from the database rather than
  // trusted from the caller. A screen that guessed "this one is a draft" would
  // otherwise be the only thing standing between an issued invoice and a
  // double-issue.
  const guard = canIssue({
    docType: doc.docType,
    status: doc.status,
    lineCount: lines.length,
    hasCounterparty: Boolean(doc.customerId ?? doc.supplierId),
    allocatedPaise: doc.allocatedPaise,
    isReferenced: doc.linkedChildDocIds.length > 0,
  });
  if (!guard.ok) {
    throw new IssueTransitionError('NOT_ISSUABLE', guard.message);
  }

  // The locked place-of-supply decision, enforced at the ONLY point a document
  // can become ISSUED.
  //
  // Enforcing it here rather than only in the create path matters: a draft saved
  // before the customer had a state, or before the user chose one, is a perfectly
  // ordinary thing to have sitting in the list. Issuing is what creates the tax
  // liability, so this is where "we do not know the place of supply" has to stop
  // the document — with a message naming what the user must supply.
  // Read ONCE, at the top. Two things need the business row - the place-of-supply
  // classification and the deliver-and-invoice setting - and reading it twice
  // invites the two to disagree about which business a document belongs to.
  const business = await tx.businesses.get(doc.businessId);

  if (docSpec(doc.docType).taxDocument) {
    // The business state is needed ONLY to classify the already-resolved code as
    // intra- or inter-state; it never chooses it. Read from the business row,
    // never assumed, so a second company in another state is taxed correctly.
    const resolution = resolvePlaceOfSupply({
      businessStateCode: business?.stateCode ?? '',
      customerStateCode: doc.placeOfSupplyCode,
    });
    if (!resolution.resolved) {
      throw new IssueTransitionError(
        'PLACE_OF_SUPPLY_REQUIRED',
        `Place of supply is required before issuing this document (${resolution.reason}). ` +
          'Open the document, choose the state where the supply occurs, and issue it again.',
      );
    }
  }

  // The movements and the status change, and the mutations that publish them,
  // are ONE Dexie operation each rather than four sequential awaits.
  //
  // This ordering is not a performance choice. Dexie commits a transaction as
  // soon as its last promise settles, and a long read loop (`buildStock...`
  // awaits a product and a link per line) followed by a write, then a further
  // write, can let the transaction close underneath the later one. The visible
  // symptom is `PrematureCommitError` with the movements COMMITTED and the
  // outbox EMPTY — the worst possible outcome, because stock has moved and no
  // other device will ever hear about it.
  //
  // So: record the mutations while the transaction is provably still open, and
  // keep the business writes that follow to the minimum.
  const movements = await buildStockMovementsForIssue(tx, doc, lines, user, now, business);
  if (movements.length) {
    await tx.stockTransactions.bulkAdd(movements);
  }

  // ── SYNC, in the SAME transaction ─────────────────────────────────────
  //
  // The status change and the movements it caused travel TOGETHER. Publishing
  // the ISSUED status without the movements would leave the other device showing
  // an issued invoice against stock it believes is still on hand; publishing the
  // movements without the status would deduct stock for a document the device
  // still believes is a draft.
  //
  // HERE, immediately after the movements, rather than at the end — see the note
  // above. Written later, the transaction can have already closed and this write
  // either throws or lands outside the transaction, leaving stock moved and the
  // outbox empty. Because it is in the SAME transaction as the movements, that
  // state is unreachable: either both commit or neither does.
  // Computed ONCE and used for the outbox and the row. Deriving it three times
  // from the same `doc` happened to agree, but three copies of "what version is
  // this" is three chances to publish a change whose version does not match the
  // version stored on the row.
  const issuedVersion = nextVersion(doc.syncVersion);
  const issued = {
    ...doc,
    status: 'ISSUED' as const,
    issuedAt: now,
    updatedAt: now,
    updatedBy: user.id,
    syncVersion: issuedVersion,
  };
  await recordBusinessMutations(tx, [
    {
      businessId: doc.businessId,
      deviceId,
      entityType: 'document',
      entityId: documentId,
      operation: 'UPSERT',
      version: issuedVersion,
      changedAt: now,
      // The change id is minted per mutation by the recorder, so this issue can
      // never share an id with the create of the same document.
      //
      // It has been wrong twice. First the id was the row id alone, and this
      // issue was collapsed onto the create by the provider: the invoice stayed a
      // DRAFT on every other device, silently, with the sync reporting success.
      // Then it was the row id plus the VERSION, which fixed that and introduced
      // the opposite failure — two machines editing the same document from the
      // same base both compute v2, so two different edits shared one id and the
      // second was dropped. A minted id is unique without anyone agreeing on a
      // number, and a retry re-sends the stored one.
      payload: issued as unknown as Record<string, unknown>,
    },
    ...movements.map((movement) => ({
      businessId: movement.businessId,
      deviceId,
      entityType: 'stockTransaction' as const,
      entityId: movement.id,
      operation: 'UPSERT' as const,
      version: 1,
      changedAt: now,
      // The movement is append-only, and its outbox mutation has its own minted
      // ULID. Retries republish this persisted entry rather than recording again.
      payload: movement as unknown as Record<string, unknown>,
    })),
  ]);

  await tx.documents.update(documentId, {
    status: 'ISSUED',
    // `issuedAt` only: the Document row has no `issuedBy` column, and adding one
    // for a single field would mean a schema migration to record who pressed the
    // button. The audit row below already carries the user, which is the record
    // anyone would actually read.
    issuedAt: now,
    updatedAt: now,
    updatedBy: user.id,
    // PERSISTED, and this is the part that was missing. The outbox published
    // version N under a change id derived from N; if the row still says N-1 then
    // the next operation derives N again, reuses the id just published, and the
    // provider collapses the NEXT real change as a duplicate. A counter that is
    // only ever computed and discarded hands out the same id forever.
    syncVersion: issuedVersion,
  });

  await tx.auditLogs.add({
    id: ulid(),
    businessId: doc.businessId,
    entityType: 'DOCUMENT',
    entityId: documentId,
    action: 'ISSUE',
    before: JSON.stringify({ status: 'DRAFT' }),
    after: JSON.stringify({
      status: 'ISSUED',
      docNumber: doc.docNumber,
      // Recorded so an audit can see the stock effect of issuing, not just the
      // state change. The ledger itself remains the source of truth.
      stockMovements: movements.length,
    }),
    amountPaise: doc.grandTotalPaise,
    userId: user.id,
    userName: user.name,
    occurredAt: now,
  });

  return { id: documentId, docNumber: doc.docNumber, status: 'ISSUED' };
}

/**
 * The movements issuing this document owns, one per line.
 *
 * A line is skipped when `stockOwnerFor` says the document is not the owner:
 *   - `NONE`   — a draft-only type such as a quotation, or already issued.
 *   - `DISPATCHED` — the line consumes a Delivery Note, which deducted the goods
 *     when IT was issued. Deducting again is the double-deduction bug.
 *
 * Only ISSUED documents reach this function, so the status branch of
 * `stockOwnerFor` has already been satisfied; it is still passed, because the
 * function is the definition and this is a caller.
 *
 * NOT `async`, deliberately. An `async` function returns a promise Dexie does
 * not track, and Dexie uses that tracking to decide when a transaction may
 * commit. An untracked read loop lets it conclude the transaction is finished
 * while reads are still outstanding, and the write that follows lands outside the
 * transaction — or throws `PrematureCommitError` — leaving stock movements
 * COMMITTED with the outbox EMPTY. The goods would have left the shop on this
 * device with no other device ever hearing about it, and the ledger is
 * append-only, so nothing would ever correct it.
 *
 * Returning Dexie's own promise chain keeps the transaction open for exactly as
 * long as the reads take. The RULES are untouched; only the promise plumbing is.
 */
function buildStockMovementsForIssue(
  tx: IssueTransitionTables,
  doc: Document,
  lines: readonly DocumentLine[],
  user: { id: string; name: string },
  now: string,
  business: Business | undefined,
): Promise<StockTransaction[]> {
  const spec = docSpec(doc.docType);
  if (!spec.movesStock || !spec.stockDirection) {
    return Dexie.Promise.resolve([] as StockTransaction[]);
  }

  const sign = spec.stockDirection === 'IN' ? 1 : -1;
  const out: StockTransaction[] = [];

  // Read per line, SEQUENTIALLY, and the sequencing is load-bearing.
  //
  // `Promise.all` looks like an improvement here and is a bug: it returns a plain
  // promise, Dexie loses track of the transaction the moment it is awaited, and
  // every write after it can land outside the transaction. Dexie is aware of its
  // OWN promises and of nothing else.
  //
  // Folding each step with `.then()` — rather than awaiting — keeps every link
  // in a Dexie promise, so the transaction stays open for exactly as long as the
  // reads take. `Dexie.waitFor` on an `async` helper is the wrong tool: it re-
  // throws rather than fixing the liveness, and it hides the real ordering.
  let chain: Dexie.Promise<null> = Dexie.Promise.resolve(null);

  for (const line of lines) {
    const productId = line.productId;
    const stockLocationId = line.stockLocationId;
    if (!productId || !stockLocationId) continue;
    chain = chain.then(() =>
      // The product decides whether stock is tracked at all. A service item never
      // appears in the ledger, and a line that lost its product row cannot be
      // attributed to one.
      tx.products.get(productId).then((product) => {
        if (!product || !product.isStockTracked) return null;
        return sourceLineDocType(tx, doc.id, line.id).then((sourceDocType) => {
          if (
            stockOwnerFor({
              docType: doc.docType,
              status: 'ISSUED',
              sourceLineDocType: sourceDocType,
              // The BUSINESS SETTING, not a constant. The locked default is true,
              // but a business that always raises a Delivery Note must be able to
              // say so, and hard-coding it here would make it unreachable.
              allowInvoiceWithoutDelivery: business?.documentSettings.allowInvoiceWithoutDelivery,
            }) !== 'SELF'
          ) {
            return null;
          }
          const qty = (line.quantityMilli + line.freeQuantityMilli) * sign;
          if (qty === 0) return null;
          out.push({
            id: ulid(),
            businessId: doc.businessId,
            productId,
            stockLocationId,
            documentId: doc.id,
            documentLineId: line.id,
            documentType: doc.docType,
            docDate: doc.docDate,
            direction: spec.stockDirection ?? 'OUT',
            qtyChangeMilli: qty,
            ratePaise: line.ratePaise,
            reason: movementReasonFor(doc.docType, spec.stockDirection ?? 'OUT'),
            balanceAfterMilli: 0,
            createdAt: now,
            createdBy: user.id,
          });
          return null;
        });
      }),
    );
  }

  return chain.then(() => out);
}

/**
 * The docType of the document this line CONSUMES, or null when it consumes none.
 *
 * LINK DIRECTION — the detail that is easy to get backwards, and did.
 * A link is written as `from*` = the document being created and `to*` = the
 * source it consumes. So to ask "where did THIS line come from", the query keys on
 * `fromDocumentId`/`fromLineId` — this document's own line — and the ANSWER is
 * read from `toDocumentId`.
 *
 * Reading it the other way round looks like it works: it returns a document, and
 * the guard quietly lets the invoice deduct a second time. Which is precisely the
 * double-deduction this decision exists to prevent, so the tests below exist.
 *
 * A standalone invoice (no Sales Order, no Delivery Note) has no such link, and
 * correctly resolves to null: with nothing consumed, the invoice may own the
 * movement itself.
 */
function sourceLineDocType(
  tx: IssueTransitionTables,
  documentId: string,
  lineId: string,
): Promise<DocType | null> {
  // Returns the Dexie promise ITSELF rather than wrapping it in an `async`
  // function.
  //
  // This is not a style preference. Dexie knows which of its own promises are
  // still running, and it uses that to decide when a transaction may commit. An
  // `async` wrapper returns a NEW, untracked promise: Dexie can no longer see
  // that the query is outstanding, decides the transaction is finished, and the
  // next write either lands outside the transaction or throws
  // `PrematureCommitError` — with the stock movements already committed and the
  // outbox empty, which is the worst state this system can be in.
  //
  // `.then()` keeps the chain in Dexie's own promise, so the transaction stays
  // open for exactly as long as the reads do.
  return tx.documentLinks
    .where('[fromDocumentId+fromLineId]')
    .equals([documentId, lineId])
    .toArray()
    .then((links) => {
      const first: DocumentLink | undefined = links[0];
      if (!first) return null;
      return tx.documents.get(first.toDocumentId).then((source) => source?.docType ?? null);
    });
}

/**
 * Whether a document type moves stock at all, for the UI to explain itself.
 *
 * Exported so the screen can say "issuing this will deduct stock" without
 * duplicating the registry, which is the kind of copy that goes stale.
 */
export function movesStockWhenIssued(docType: Document['docType']): boolean {
  return docSpec(docType).movesStock;
}

/**
 * Cancel an ISSUED document, posting the INVERSE stock movement.
 *
 * WHY AN INVERSE MOVEMENT AND NOT A DELETE
 * ---------------------------------------
 * The stock ledger is append-only. That is a deliberate property: a balance that
 * can be silently edited is a balance nobody can reconcile, and a shop that
 * cannot reconcile its stock will not trust the software.
 *
 * So cancelling does not erase the original movement. It posts the OPPOSITE one.
 * The history then shows the sale and its reversal, and the net effect on stock
 * is zero — which is both auditable and correct, rather than a row that was there
 * yesterday and is not today with nothing to show for it.
 *
 * Only the lines this document OWNED are reversed. A line that consumed a
 * Delivery Note is not reversed here, because the Delivery Note posted the
 * original movement and owns reversing it. Reversing both would zero the stock
 * twice over — the same double-deduction bug, in the opposite direction.
 */
export async function cancelIssuedDocument(
  tx: IssueTransitionTables,
  documentId: string,
  user: { id: string; name: string },
  now: string,
  /**
   * Why it was cancelled.
   *
   * Positionally required and explicitly nullable: a caller with no reason
   * writes `undefined`, because making this `reason?:` would let a caller skip
   * the slot entirely — and the slot after it is the device id.
   */
  reason: string | undefined,
  /**
   * This install's device. Every mutation below is attributed to it.
   *
   * REQUIRED, with NO default. It used to default to `'unknown-device'`, and that
   * default made the one mistake that matters invisible: a caller that simply
   * left the argument out got a write that worked. Every mutation — and every
   * conflict record built from one — was then attributed to a device id, and
   * `toWireChange` puts that id on the wire, so every receiving device was told
   * the change came from an install the cloud has never heard of. "Which device
   * caused this?" has no answer, and that is the one question the device id
   * exists to answer. Without a default, forgetting it is a compile error
   * instead of a falsehood in the ledger.
   */
  deviceId: string,
): Promise<{ readonly id: string; readonly reversed: number }> {
  const doc = await tx.documents.get(documentId);
  if (!doc) {
    throw new IssueTransitionError('UNKNOWN_DOCUMENT', `No document with id "${documentId}".`);
  }
  if (doc.status !== 'ISSUED') {
    throw new IssueTransitionError(
      'NOT_CANCELLABLE',
      'Only an issued document can be cancelled. This one is a draft — delete it instead.',
    );
  }

  const lines = await tx.documentLines.where('documentId').equals(documentId).toArray();
  const business = await tx.businesses.get(doc.businessId);
  const spec = docSpec(doc.docType);
  const reversals: StockTransaction[] = [];

  if (spec.movesStock && spec.stockDirection) {
    const inverse = inverseDirection(spec.stockDirection);

    for (const line of lines) {
      if (!line.productId || !line.stockLocationId) continue;
      const product = await tx.products.get(line.productId);
      if (!product || !product.isStockTracked) continue;

      // Only reverse what THIS document posted. A line consuming a Delivery Note
      // is `DISPATCHED`, and the delivery note owns reversing its own movement.
      const sourceDocType = await sourceLineDocType(tx, doc.id, line.id);
      if (
        stockOwnerFor({
          docType: doc.docType,
          status: 'ISSUED',
          sourceLineDocType: sourceDocType,
          // The same BUSINESS SETTING the issue path used, so cancelling reverses
          // exactly the movements issuing posted - no more, no fewer.
          allowInvoiceWithoutDelivery: business?.documentSettings.allowInvoiceWithoutDelivery,
        }) !== 'SELF'
      ) {
        continue;
      }

      const qty = line.quantityMilli + line.freeQuantityMilli;
      if (qty === 0) continue;

      reversals.push({
        id: ulid(),
        businessId: doc.businessId,
        productId: line.productId,
        stockLocationId: line.stockLocationId,
        documentId: doc.id,
        documentLineId: line.id,
        documentType: doc.docType,
        docDate: doc.docDate,
        direction: inverse,
        // The whole sign flip. The original OUT of 2000 becomes an IN of 2000.
        qtyChangeMilli: qty,
        ratePaise: line.ratePaise,
        // Distinct reason, so a stock report can tell a sale from its reversal
        // rather than netting them into "no movement" and hiding both.
        reason: 'CANCELLATION',
        balanceAfterMilli: 0,
        createdAt: now,
        createdBy: user.id,
      });
    }
  }

  if (reversals.length) await tx.stockTransactions.bulkAdd(reversals);

  const cancelledVersion = nextVersion(doc.syncVersion);
  await tx.documents.update(documentId, {
    status: 'CANCELLED',
    cancelledAt: now,
    cancelledBy: user.id,
    updatedAt: now,
    updatedBy: user.id,
    // Persisted for the same reason as the issue: the change id below is derived
    // from this number, so a row that is not advanced hands out the same id twice.
    syncVersion: cancelledVersion,
  });

  await tx.auditLogs.add({
    id: ulid(),
    businessId: doc.businessId,
    entityType: 'DOCUMENT',
    entityId: documentId,
    action: 'CANCEL',
    before: JSON.stringify({ status: 'ISSUED', docNumber: doc.docNumber }),
    after: JSON.stringify({
      status: 'CANCELLED',
      // Recorded so an audit can see the stock effect, not just the state change.
      stockReversals: reversals.length,
      ...(reason ? { reason } : {}),
    }),
    amountPaise: doc.grandTotalPaise,
    userId: user.id,
    userName: user.name,
    occurredAt: now,
  });

  // ── SYNC, in the SAME transaction ─────────────────────────────────────
  //
  // The cancellation and its inverse movements travel together. Publishing the
  // CANCELLED status without the reversals would leave the other device
  // cancelling an invoice while still believing its stock was sold — and the
  // stock would stay deducted forever, with no movement to show for it.
  const cancelled = {
    ...doc,
    status: 'CANCELLED' as const,
    cancelledAt: now,
    cancelledBy: user.id,
    syncVersion: cancelledVersion,
  };
  await recordBusinessMutations(tx, [
    {
      businessId: doc.businessId,
      deviceId,
      entityType: 'document',
      entityId: documentId,
      operation: 'UPSERT',
      version: cancelledVersion,
      changedAt: now,
      // THE REAL-MONEY BUG THAT PUT THIS COMMENT HERE.
      //
      // The id used to be `document:<id>`, identical to both the create and the
      // issue. The provider keys idempotency on `changeId` alone, so it collapsed
      // the cancellation as a duplicate of the original invoice: the goods came
      // back, stock was returned locally, the reversal movements travelled — and
      // every other device kept showing a LIVE invoice for goods that had already
      // been returned to the shop. Nothing errored. The sync reported success the
      // whole time.
      //
      // It is minted per mutation now, so a cancellation can never be mistaken
      // for the document it cancels — and a retry re-sends the STORED id, so it
      // still cannot double-return the stock.
      payload: cancelled as unknown as Record<string, unknown>,
    },
    ...reversals.map((movement) => ({
      businessId: movement.businessId,
      deviceId,
      entityType: 'stockTransaction' as const,
      entityId: movement.id,
      operation: 'UPSERT' as const,
      version: 1,
      changedAt: now,
      // The reversal is a NEW movement, never a deletion of the original. Its
      // outbox identity is minted once and persisted with the payload.
      payload: movement as unknown as Record<string, unknown>,
    })),
  ]);

  return { id: documentId, reversed: reversals.length };
}

